import { Box, Divider, ListItemIcon, Stack, SxProps, Theme, Typography } from '@mui/material'
import { C1Transitions } from 'c1ui/theme/C1Transitions'
import { C1Dot } from 'components/core/dot'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1Link } from 'components/core/links/C1Link'
import { getTransition, useMutedDotCache } from 'components/pages/sidebar/CollapsingSidebar'
import { Route } from 'lib/routes'
import { useRouter } from 'next/router'
import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { FlyoutSafeArea } from './FlyoutSafeArea'
import { getNavItemHighlightWidth, navItemMinHeightPx, navItemWrapperXPaddingPx, navTransitionDuration, NavModule, navItemWrapperYPaddingPx } from './types'
import { useFlyoutSafeArea } from './useFlyoutSafeArea'

/**
 * Context for flyout close signaling.
 * Allows items inside a flyout to signal the parent flyout to close.
 */
type FlyoutContextType = {
    closeFlyout: () => void
}

const FlyoutContext = createContext<FlyoutContextType | null>(null)

/**
 * Hook to access the flyout close function.
 * Returns null if not inside a flyout.
 */
export const useFlyoutClose = (): (() => void) | null => {
    const context = useContext(FlyoutContext)
    return context?.closeFlyout ?? null
}

type NavItemDisplayProps = {
    /** Display label (ReactNode, typically a FormattedMessage) */
    label: ReactNode
    /** Icon element (caller handles styling) */
    icon: ReactNode
    /** Whether this item is currently active */
    isActive?: boolean
    /** Whether to show the notification dot */
    showDot?: boolean
    /** Custom indicator component (shown instead of dot when provided) */
    customIndicator?: ReactNode
    /** Show separator line before this item */
    showSeparator?: boolean
    /** Link href (for navigation items) */
    href?: Route
    /** Click handler */
    onClick?: () => void
    /** Whether the sidebar is collapsed (affects icon-only layout) */
    isCollapsed?: boolean
    /** Whether this is a top-level nav item (affects width animation) */
    isTopLevel?: boolean
    /** Children rendered inside the outer wrapper (e.g., flyout menus) */
    children?: ReactNode
    /** Additional sx props for the outer wrapper */
    wrapperSx?: SxProps<Theme>
    /** Ref to the flyout element - if provided, enables triangular safe area for flyout hover */
    flyoutRef?: HTMLDivElement | null
    /** Whether this item's flyout is persistently shown (disables dynamic hover/close behaviors) */
    isPersistent?: boolean
}

/**
 * Dumb display component for nav items.
 * Used by both NavItem (links) and NavActionButton (actions).
 */
export const NavItemDisplay: FC<NavItemDisplayProps> = ({
    label,
    icon,
    isActive = false,
    showDot = false,
    customIndicator,
    showSeparator = false,
    href,
    onClick,
    isCollapsed = false,
    isTopLevel = false,
    children,
    wrapperSx,
    flyoutRef,
    isPersistent = false,
}) => {
    const {
        wrapperRef,
        mousePosition,
        shouldShowSafeArea: hookShouldShowSafeArea,
        handleMouseMove,
        handleMouseEnter,
        handleMouseLeave,
    } = useFlyoutSafeArea()

    const [forceClosed, setForceClosed] = useState(false)
    const parentFlyoutClose = useFlyoutClose()

    const closeFlyout = useCallback(() => {
        // Don't close persistent flyouts
        if (isPersistent) return
        setForceClosed(true)
    }, [isPersistent])

    const handleClick = useCallback(() => {
        onClick?.()
        // Don't close parent flyout when clicking links in persistent flyouts
        if (href && parentFlyoutClose && !isPersistent) {
            parentFlyoutClose()
        }
    }, [onClick, href, parentFlyoutClose, isPersistent])

    // Reset forceClosed on mouse enter so flyout can reopen on next hover
    const handleWrapperMouseEnter = useCallback(() => {
        handleMouseEnter()
        if (forceClosed) {
            setForceClosed(false)
        }
    }, [handleMouseEnter, forceClosed])

    const handleWrapperMouseLeave = useCallback(() => {
        handleMouseLeave()
        // Don't reset forceClosed here - keep it closed until next deliberate hover
    }, [handleMouseLeave])

    const wrapperProps = href
        ? { component: C1Link, href, onClick: handleClick }
        : { onClick: handleClick }

    // Disable mouse tracking for persistent flyouts - they don't need safe areas
    const shouldTrackMouse = !!flyoutRef && !!children && !isPersistent
    const shouldShowSafeArea = shouldTrackMouse && hookShouldShowSafeArea

    const content = (
        <>
            {showSeparator && (
                <Divider sx={{ my: 1 }} />
            )}
            {/* Must be a plain div (not C1Link) to hold the ref for safe area calculation */}
            <Box
                ref={shouldTrackMouse ? wrapperRef : undefined}
                onMouseMove={shouldTrackMouse ? handleMouseMove : undefined}
                onMouseEnter={shouldTrackMouse ? handleWrapperMouseEnter : undefined}
                onMouseLeave={shouldTrackMouse ? handleWrapperMouseLeave : undefined}
                component="div"
                sx={{
                    position: 'relative',
                    // For non-persistent flyouts, control visibility via hover
                    ...(!isPersistent && {
                        '& > .nav-flyout': {
                            visibility: 'hidden',
                            pointerEvents: 'none',
                            zIndex: 1250,
                        },
                        '&:hover > .nav-flyout, &:focus-within > .nav-flyout': {
                            visibility: forceClosed ? 'hidden' : 'visible',
                            pointerEvents: forceClosed ? 'none' : 'auto',
                            zIndex: 1260, // Above persistent flyouts
                        },
                    }),
                    // Direct child selector avoids highlighting nested flyout items
                    '&:hover > .nav-item-clickable > .nav-item-highlight': {
                        backgroundColor: isActive ? 'primary.light' : 'primary.extraLight',
                    },
                    ...wrapperSx,
                }}
            >
                {shouldShowSafeArea && (
                    <FlyoutSafeArea
                        wrapperRef={wrapperRef}
                        flyoutRef={flyoutRef}
                        mousePosition={mousePosition}
                    />
                )}
                <Box
                    {...wrapperProps}
                    className="nav-item-clickable"
                    sx={{
                        position: 'relative',
                        display: 'block',
                        textDecoration: 'none',
                        color: 'inherit',
                        cursor: 'pointer',
                        padding: `${navItemWrapperYPaddingPx}px ${navItemWrapperXPaddingPx}px`,
                    }}
                >
                    <Box
                        className="nav-item-highlight"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            borderRadius: 1,
                            py: 1,
                            px: 2,
                            minHeight: navItemMinHeightPx,
                            backgroundColor: isActive ? 'primary.light' : 'transparent',
                            position: 'relative',
                            ...(isTopLevel && {
                                width: getNavItemHighlightWidth(isCollapsed),
                                transition: `width ${navTransitionDuration} ${C1Transitions.easing.linearSpringA}`,
                                overflow: 'hidden',
                            }),
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                                mr: 2,
                                ...(isTopLevel && {
                                    transform: isCollapsed ? 'translateX(-2px)' : 'translateX(0px)',
                                    ...getTransition('transform'),
                                }),
                                position: 'relative',
                            }}
                        >
                            {icon}
                            {isTopLevel && isCollapsed && (customIndicator || (showDot && <C1Dot />)) && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -8,
                                        right: -7,
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {customIndicator || (showDot && <C1Dot />)}
                                </Box>
                            )}
                        </ListItemIcon>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            flex={1}
                            sx={{
                                opacity: isCollapsed ? 0 : 1,
                                ...(isTopLevel && {
                                    width: getNavItemHighlightWidth(false),
                                    minWidth: getNavItemHighlightWidth(false),
                                }),
                                overflow: 'hidden',
                                transition: `opacity ${navTransitionDuration} ${C1Transitions.easing.linearSpringA}`,
                                pointerEvents: isCollapsed ? 'none' : 'auto',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <Typography
                                fontWeight={isActive ? 500 : 400}
                                color={isActive ? 'primary.dark' : 'text.primary'}
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {label}
                            </Typography>
                        </Stack>
                        {/* Indicator for expanded state (complements collapsed indicator above) */}
                        {((isTopLevel && !isCollapsed) || !isTopLevel) && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: `${navItemWrapperXPaddingPx + 4}px`,
                                    transform: 'translateY(-50%)',
                                    pointerEvents: 'none',
                                }}
                            >
                                {customIndicator || (showDot && <C1Dot />)}
                            </Box>
                        )}
                    </Box>
                </Box>
                {children}
            </Box>
        </>
    )

    const contextValue = useMemo(() => ({ closeFlyout }), [closeFlyout])

    if (children) {
        return (
            <FlyoutContext.Provider value={contextValue}>
                {content}
            </FlyoutContext.Provider>
        )
    }

    return content
}

type NavItemProps = {
    item: NavModule
    /** Display label (ReactNode) */
    label: ReactNode
    /** Notification count (optional) */
    count?: number
    /** Show separator line before this item */
    showSeparator?: boolean
    /** Called when item is clicked */
    onClick?: () => void
}

/**
 * Smart navigation item component that handles route matching and dot state.
 * Used for link-based navigation items in flyouts and overlays.
 */
export const NavItem: FC<NavItemProps> = ({
    item,
    label,
    count,
    showSeparator,
    onClick,
}) => {
    const router = useRouter()
    const [dotIsMuted, setDotIsMuted] = useMutedDotCache(item.id)
    const prevCount = useRef(count)

    // Unmute dot when count increases
    useEffect(() => {
        if (count === undefined) return
        if (prevCount.current !== undefined && prevCount.current < count) {
            setDotIsMuted(false)
        }
        prevCount.current = count
    }, [count, item.id, setDotIsMuted])

    const paths = [item.pathname, ...(item.selectedPaths || [])]
    const isActive = paths.some((path) => {
        if (router.pathname === path || router.asPath === path) {
            return true
        }
        // selectedPaths use prefix matching; base pathname uses exact match only
        // to avoid /admin/applications matching /admin/applications/unmanaged-apps
        if (item.selectedPaths?.includes(path)) {
            const regex = RegExp(`^\\${path}(?:/|$).*$`, 'g')
            return router.pathname.match(regex) !== null || router.asPath.match(regex) !== null
        }
        return false
    })

    const shouldShowDot = count !== undefined && count > 0 && !dotIsMuted

    const handleClick = (): void => {
        if (count !== undefined) {
            setDotIsMuted(true)
        }
        onClick?.()
    }

    const Icon = item.icon

    return (
        <NavItemDisplay
            label={label}
            icon={
                <C1Icon
                    icon={Icon}
                    size="sm"
                    sx={{ color: isActive ? 'primary.extraDark' : 'text.secondary' }}
                />
            }
            isActive={isActive}
            showDot={shouldShowDot && !item.customIndicator}
            customIndicator={item.customIndicator ? <item.customIndicator /> : undefined}
            showSeparator={showSeparator}
            href={item.pathname}
            onClick={handleClick}
        />
    )
}
